import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { readChecklistView, withChecklistView } from '@dataroom/shared';
import type { ChecklistViewChange } from '@dataroom/shared';
import { useRoomDocuments } from '@/api/hooks';
import { DueDiligenceCard } from '@/components/DueDiligenceCard';
import type { RoomLocationState } from '@/lib/room';
import { useRoomContext } from './context';

/** PRD-0004 §3f — the phase-grouped requested-documents checklist (management
 * view). While the room has no phases it offers "Set up checklist…", which opens
 * the shell's checklist-source dialog. The view lives in the URL: the item filter
 * (`?filter=missing|waiting|covered|na|all`, default missing — so the rooms
 * dashboard's "Still missing" lands straight on a room's still-due items), the
 * phase (`?phase=`) and the search (`?q=`). */
export function RoomChecklistPage() {
  const { roomId, openChecklistSetup } = useRoomContext();
  // The card shows a linked item's document name rather than a bare id.
  const documents = useRoomDocuments(roomId);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // One-shot: arriving from "Release Phase 1 now?" → Yes focuses the phase dates.
  const focusPhaseDates = Boolean((location.state as RoomLocationState | null)?.focusPhaseDates);
  const { filter, phaseId, query } = readChecklistView(params, 'missing');
  /** Write a view change — any of filter, phase and search — in ONE setParams
   * (see withChecklistView: two calls in one handler would clobber each other). */
  const setView = (next: ChecklistViewChange) =>
    setParams((prev) => withChecklistView(prev, next), { replace: true });
  return (
    <DueDiligenceCard
      roomId={roomId}
      documents={documents.data ?? []}
      onSetUpChecklist={openChecklistSetup}
      filter={filter}
      phaseId={phaseId}
      query={query}
      onViewChange={setView}
      onReviewDocument={(documentId) =>
        navigate(`/rooms/${roomId}/review?doc=${encodeURIComponent(documentId)}`)
      }
      focusPhaseDates={focusPhaseDates}
      onPhaseDatesFocused={() =>
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
      }
    />
  );
}
